import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { T } from "@repo/transifex";
import { Button, DialogPayment } from "~/components";
import { useOrder } from "~/hooks/queries/orders";
import {
  useAbortTransaction,
  useTransactionStatus
} from "~/hooks/queries/terminals";
import { useOpenCashDrawer } from "~/hooks";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useIsCardPaymentRecovering } from "~/providers/store/basket/hooks/use-basket";
import { SECOND_MS } from "~/utils/constants";

import { TerminalReadyIcon } from "./TerminalReadyIcon";
import { CardPaymentDialog } from "./card-payment-dialog";
import { type CardPaymentProps } from "./card-payment.types";

const REFETCH_BUTTON_DELAY_MS = 10 * SECOND_MS;

export const AdyenCardPayment = ({
  orderId,
  totalPriceFormatted,
  isSplitPaymentActive
}: CardPaymentProps) => {
  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get("serviceId");
  const shouldPoll = searchParams.get("poll");

  const [showRefetchButton, setShowRefetchButton] = useState(false);

  const openCashDrawer = useOpenCashDrawer();

  const { data: order, status, refetch, isFetching } = useOrder(orderId);
  const { data: till } = useCurrentTill();
  const isRecovering = useIsCardPaymentRecovering();

  const terminalId = till?.terminalId ?? undefined;

  const { mutate: abortTransaction, status: abortTransactionStatus } =
    useAbortTransaction();

  useTransactionStatus(
    { serviceId, orderId, terminalId },
    shouldPoll === "true"
  );

  // Show refetch button after delay once orderId is available
  useEffect(() => {
    if (!order?.id) return;

    const timer = setTimeout(() => {
      setShowRefetchButton(true);
    }, REFETCH_BUTTON_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [order?.id]);

  // Hide refetch button when order status changes
  useEffect(() => {
    if (order?.status && order.status !== "pending") {
      setShowRefetchButton(false);
    }
  }, [order?.status]);

  if (status === "error" || order?.status === "failed") {
    return <DialogPayment status="error" />;
  }

  if (order?.status === "cancelled") {
    return <DialogPayment status="cancelled" />;
  }

  if (order?.status === "paid") {
    return <DialogPayment orderId={order.id} status="success" />;
  }

  return (
    <CardPaymentDialog
      title={
        isRecovering ? (
          <T _str="Connection lost, checking the payment" />
        ) : (
          <T _str="Awaiting payment on terminal" />
        )
      }
      description={totalPriceFormatted}
      icon={<TerminalReadyIcon />}
    >
      <CardPaymentDialog.CancelButton
        disabled={!terminalId}
        loading={abortTransactionStatus === "pending"}
        onClick={() => {
          if (!terminalId) {
            throw new Error("Cannot abort transaction: missing terminalId");
          }
          abortTransaction({
            ...(orderId && { orderId }),
            serviceId
          });

          if (isSplitPaymentActive) {
            openCashDrawer("automatic");
          }
        }}
      />
      {showRefetchButton ? (
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm text-text-secondary">
            <T _str="Taking too long? Try refreshing" />
          </p>
          <Button
            variant="secondary"
            loading={isFetching}
            onClick={() => refetch()}
          >
            <T _str="Refresh order" />
          </Button>
        </div>
      ) : null}
    </CardPaymentDialog>
  );
};
